import { useCallback } from 'react';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { isClientSideValidationError } from '@atlassian/jira-business-error-handling/src/utils/client-side-validation-error/index.tsx';
import { isUnsupportedOperationError } from '@atlassian/jira-business-error-handling/src/utils/unsupported-operation-error/index.tsx';
import { VALIDATION_ERROR_CODES } from '@atlassian/jira-business-fields/src/common/clipboard-handler/constants.tsx';
import type { ClipboardItem } from '@atlassian/jira-business-fields/src/common/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import messages from './messages';
import type { ReportedErrorInput } from './types';

const UNSETTABLE_FIELD_TYPES = {
	issuekey: true,
	issuetype: true,
	priority: true,
	status: true,
	parent: true,
};

export const canBeReset = (field: BusinessIssueField) => {
	const isEditable = field.fieldConfig?.isEditable ?? false;
	const isRequired = field.fieldConfig?.isRequired ?? false;

	if (field.type != null && field.type in UNSETTABLE_FIELD_TYPES) {
		return false;
	}

	return isEditable && !isRequired;
};

type WrappedFormatMessage = (message: MessageDescriptor) => string;

const getPasteMessage = (error: Error, wrappedFormatMessage: WrappedFormatMessage) => {
	if (isClientSideValidationError(error)) {
		if (error.message === VALIDATION_ERROR_CODES.EMPTY_CLIPBOARD) {
			return {
				title: wrappedFormatMessage(messages.emptyClipboardTitle),
				description: wrappedFormatMessage(messages.pasteEmptyClipboardError),
			};
		}

		if (error.message === VALIDATION_ERROR_CODES.MISSING_FIELD) {
			return {
				title: wrappedFormatMessage(messages.pasteErrorTitle),
				description: wrappedFormatMessage(messages.missingFieldAssociationError),
			};
		}

		if (error.message === VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE) {
			return {
				title: wrappedFormatMessage(messages.pasteErrorTitle),
				description: wrappedFormatMessage(messages.pasteUnsupportedError),
			};
		}
	}
	if (isUnsupportedOperationError(error)) {
		return {
			title: wrappedFormatMessage(messages.pasteErrorTitle),
			description: wrappedFormatMessage(messages.pasteUnsupportedError),
		};
	}

	return {
		title: wrappedFormatMessage(messages.pasteErrorTitle),
		description: wrappedFormatMessage(messages.genericServerError),
	};
};

const getCutMessage = (error: Error, wrappedFormatMessage: WrappedFormatMessage) => {
	if (isClientSideValidationError(error) && error.message === VALIDATION_ERROR_CODES.NON_EDITABLE) {
		return {
			title: wrappedFormatMessage(messages.cutErrorTitle),
			description: wrappedFormatMessage(messages.cutNonEditableError),
		};
	}

	return {
		title: wrappedFormatMessage(messages.cutErrorTitle),
		description: wrappedFormatMessage(messages.genericServerError),
	};
};

const getCopyMessage = (error: Error, wrappedFormatMessage: WrappedFormatMessage) => ({
	title: wrappedFormatMessage(messages.copyErrorTitle),
	description: wrappedFormatMessage(messages.copyValidationError),
});

const getErrorMessage = (
	action: string,
	error: Error,
	wrappedFormatMessage: WrappedFormatMessage,
) => {
	if (action === 'paste') {
		return getPasteMessage(error, wrappedFormatMessage);
	}
	if (action === 'cut') {
		return getCutMessage(error, wrappedFormatMessage);
	}
	if (action === 'copy') {
		return getCopyMessage(error, wrappedFormatMessage);
	}
};

const uniqueFlagId = toFlagId('LIST_COPY_PASTE_FLAG');

export const useClipboardErrorReporting = (field: BusinessIssueField) => {
	const { formatMessage } = useIntl();
	const { dismissFlag, showFlag } = useFlagsService();

	const reportError = useCallback(
		({ action, error }: ReportedErrorInput) => {
			if (!error) {
				return;
			}

			const ugcSafeMessage = `Failed to ${action} a field of type ${field.type}`;

			fireErrorAnalytics({
				meta: {
					id: 'jiraClipboardController',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: ugcSafeMessage,
					fieldType: field.type,
					operation: action,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			const wrappedFormatMessage = (message: MessageDescriptor) =>
				formatMessage(message, {
					name: field.name,
				});

			const errorMessage = getErrorMessage(action, error, wrappedFormatMessage);

			if (!errorMessage) {
				return;
			}

			showFlag({
				id: uniqueFlagId,
				type: isClientSideValidationError(error) ? 'warning' : 'error',
				title: errorMessage.title,
				description: errorMessage.description,
			});
		},
		[showFlag, field.type, field.name, formatMessage],
	);

	return {
		reportError,
		dismissFlag: () => dismissFlag(uniqueFlagId),
	};
};

export const setDataToClipboard = (e: ClipboardEvent, clipboardItem: ClipboardItem | null) => {
	if (!e.clipboardData || !clipboardItem || !clipboardItem.plainText || !clipboardItem.html) {
		throw new Error('Clipboard item is not provided');
	}

	e.clipboardData.setData('text/plain', clipboardItem.plainText);
	e.clipboardData.setData('text/html', clipboardItem.html);
};
